import ParticipateCard from "@/pages/challenges/components/cards/ParticipateCard.tsx";
import NoData from "@/components/common/NoData";
import IconStar from "@/assets/icons/star-filled.svg?react";
import { useTranslation } from "react-i18next";
import ParticiateCardLoading from "./components/cards/ParticiateCardLoading";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { useChallenges } from "@/pages/challenges/api";
import { PropsWithChildren, Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { logError } from "@/components/error-hanlder/error-handler.lib.ts";
import { toast } from "sonner";
import { useInfiniteQueryFlatted } from "@/shared/hooks/useInfiniteQueryFlatted";
import InfiniteScrollList, {
  checkFetchMore,
} from "@/components/base/InfiniteScrollList";

const ParticipateListWrapper = (props: PropsWithChildren) => {
  return <div className="grid mb-4 gap-4 w-full">{props.children}</div>;
};

const ErrorHandler = () => {
  const { t } = useTranslation();

  useEffect(() => {
    toast.error(t("challenge_fetching_error"));
  }, [t]);

  return (
    <NoData
      title={t("challenge_no_data_title")}
      description={t("your_challenge_no_data_description")}
      image={<IconStar color="white" width="48" height="48" />}
    />
  );
};

export default function Participate() {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense
        fallback={
          <ParticipateListWrapper>
            {Array.from({ length: 3 }).map((_, idx) => (
              <ParticiateCardLoading key={idx} />
            ))}
          </ParticipateListWrapper>
        }
      >
        <BaseParticipate />
      </Suspense>
    </ErrorBoundary>
  );
}

function BaseParticipate() {
  const queryResult = useSuspenseInfiniteQuery(useChallenges(true));

  const participateData = useInfiniteQueryFlatted(queryResult.data);

  const { t } = useTranslation();
  return (
    <ParticipateListWrapper>
      <InfiniteScrollList
        fetchNextPage={queryResult.fetchNextPage}
        canFetchMore={checkFetchMore(queryResult)}
        isFetchingNextPage={queryResult.isFetchingNextPage}
        placeholder={Array.from({ length: 1 }).map((_, idx) => (
          <ParticiateCardLoading key={idx} />
        ))}
        className="w-full gap-4"
      >
        {participateData.length ? (
          participateData.map((item) => (
            <ParticipateCard
              id={item.id}
              key={item.id}
              logo={item.images[0]?.url ?? ""}
              title={item.name}
              targetAmount={item.target_amount}
              gainedAmount={item.gained_amount}
              status={item.status}
              chance={item.my_opportunities}
            />
          ))
        ) : (
          <div className="flex-1 flex-center min-h-[60vh]">
            <NoData
              title={t("challenge_no_data_title")}
              description={t("your_challenge_no_data_description")}
              image={<IconStar color="white" width="48" height="48" />}
            />
          </div>
        )}
      </InfiniteScrollList>
    </ParticipateListWrapper>
  );
}
